import { useNavigate } from 'react-router';
import useNotifications from '../../hooks/useNotifications/useNotifications';
import { useCallback, useState } from 'react';
import PageContainer from '../layout/PageContainer';
import { validate as validatePurchaseOrder, } from '../../models/PurchaseOrder';
import { FormFieldValue } from '../forms/FormFieldValue';
import { useMutation } from '@apollo/client/react';
import { GET_INVENTORYITEMS_QUERY } from '../../queries/InventoryItemQueries';
import PurchaseOrderForm, { PurchaseOrderFormState } from './PurchaseOrderForm';
import { ADD_PURCHASEORDER_MUTATION } from '../../mutations/PurchaseOrderMutations';
import { GET_PURCHASEORDERS_QUERY } from '../../queries/PurchaseOrderQueries';
import { type InventoryItem, validate as validateInventoryItem } from '../../models/InventoryItem';
import dayjs from "dayjs";
import { InventoryItemFormState } from "../inventoryItems/InventoryItemForm";
import { GET_ITEMS_QUERY } from "../../queries/ItemQueries";

const INITIAL_FORM_VALUES: Partial<PurchaseOrderFormState['values']> = {
  vendorId: 0,
  dateTimePurchased: dayjs(),
  inventoryItems: []
};

export default function PurchaseOrderCreate() {
  const navigate = useNavigate();

  const notifications = useNotifications();

  const [createPurchaseOrder] = useMutation(
    ADD_PURCHASEORDER_MUTATION, {
    refetchQueries: [
      { query: GET_PURCHASEORDERS_QUERY },
      { query: GET_INVENTORYITEMS_QUERY },
      { query: GET_ITEMS_QUERY }],
  });

  const [formState, setFormState] = useState<PurchaseOrderFormState>(() => ({
    values: INITIAL_FORM_VALUES,
    errors: {},
  }));

  const [inventoryItemErrors, setInventoryItemErrors] = useState<Map<number, Partial<Record<keyof InventoryItemFormState['values'], string>>>>(new Map());

  const formValues = formState.values;
  const formErrors = formState.errors;

  const setFormValues = useCallback(
    (newFormValues: Partial<PurchaseOrderFormState['values']>) => {
      setFormState((previousState) => ({
        ...previousState,
        values: newFormValues,
      }));
    },
    [],
  );

  const setFormErrors = useCallback(
    (newFormErrors: Partial<PurchaseOrderFormState['errors']>) => {
      setFormState((previousState) => ({
        ...previousState,
        errors: newFormErrors,
      }));
    },
    [],
  );

  const handleFormFieldChange = useCallback(
    (name: keyof PurchaseOrderFormState['values'], value: FormFieldValue) => {
      const validateField = async (values: Partial<PurchaseOrderFormState['values']>) => {
        const { issues } = validatePurchaseOrder(values);
        setFormErrors({
          ...formErrors,
          [name]: issues?.find((issue) => issue.path?.[0] === name)?.message,
        });
      };

      const newFormValues = { ...formValues, [name]: value };

      setFormValues(newFormValues);
      validateField(newFormValues);
    },
    [formValues, formErrors, setFormErrors, setFormValues],
  );

  const validateInventoryItems = useCallback((inventoryItems: InventoryItem[]) => {
    const updatedInventoryItemErrors = new Map<number, Partial<Record<keyof InventoryItemFormState['values'], string>>>();

    for (let inventoryItemIndex = 0; inventoryItemIndex < inventoryItems.length; inventoryItemIndex++) {
      const inventoryItem = inventoryItems[inventoryItemIndex];

      const { issues: inventoryItemIssues } = validateInventoryItem(inventoryItem, false);

      if (inventoryItemIssues && inventoryItemIssues.length > 0) {
        const inventoryItemErrors = Object.fromEntries(inventoryItemIssues.map((issue) => [issue.path?.[0], issue.message]));
        updatedInventoryItemErrors.set(inventoryItemIndex, inventoryItemErrors);
      }
    }

    if (updatedInventoryItemErrors.size > 0) {
      setInventoryItemErrors(updatedInventoryItemErrors);
      return false;
    } else {
      setInventoryItemErrors(new Map());
    }

    return true;
  }, []);

  const handleInventoryItemFormFieldChange = useCallback((inventoryItems: InventoryItem[]) => {
    validateInventoryItems(inventoryItems);
  }, [validateInventoryItems]);

  const handleFormReset = useCallback(() => {
    setFormValues(INITIAL_FORM_VALUES);
  }, [setFormValues]);

  const handleFormSubmit = useCallback(async (values: Partial<PurchaseOrderFormState['values']>, inventoryItems: InventoryItem[]) => {
    const { issues } = validatePurchaseOrder(values);

    let errors = null;
    let hasErrors = false;

    if (issues && issues.length > 0) {
      errors = Object.fromEntries(issues.map((issue) => [issue.path?.[0], issue.message]));
      hasErrors = true;
    }

    if ((inventoryItems || []).length > 0) {
      const inventoryItemsValid = validateInventoryItems(inventoryItems);

      if (!inventoryItemsValid) {
        hasErrors = true;
      }
    }
    else {
      errors = {
        ...errors,
        inventoryItems: 'You must add at least one item.'
      };

      hasErrors = true;
    }

    if (hasErrors) {
      setFormErrors(errors || {});
      return;
    }

    setFormErrors({});
    setInventoryItemErrors(new Map());

    try {
      const { data: result } = await createPurchaseOrder({
        variables: {
          input: {
            id: 0,
            dateTimePurchased: values.dateTimePurchased || dayjs(),
            vendorId: +(values.vendorId || 0),
            inventoryItems: inventoryItems
          }
        }
      });

      if (result?.added) {
        notifications.show('Purchase order created successfully.', {
          severity: 'success',
          autoHideDuration: 3000,
        });

        navigate('/dashboard/purchase-orders');
      } else {
        notifications.show(
          'Failed to create purchase order. Reason: Unknown.',
          {
            severity: 'error',
            autoHideDuration: 3000,
          },
        );
      }
    } catch (createError) {
      notifications.show(
        `Failed to create purchase order. Reason: ${(createError as Error).message}`,
        {
          severity: 'error',
          autoHideDuration: 3000,
        },
      );
      throw createError;
    }
  }, [createPurchaseOrder, navigate, notifications, setFormErrors, validateInventoryItems]);

  return (
    <PageContainer
      title="New Purchase Order"
      breadcrumbs={[{ title: 'Purchase Orders', path: '/dashboard/purchase-orders' }, { title: 'New' }]}
    >
      <PurchaseOrderForm
        formState={formState}
        inventoryItemErrors={inventoryItemErrors}
        onFieldChange={handleFormFieldChange}
        onInventoryItemFieldChange={handleInventoryItemFormFieldChange}
        onSubmit={handleFormSubmit}
        onReset={handleFormReset}
        submitButtonLabel="Create"
      />
    </PageContainer>
  );
}
